import { TeamMemberArtifactDto } from './team-member-media.service';

export const ThemeVisualBlockKeys = [
  'team1Left',
  'team1Right',
  'team2Left',
  'team2Right',
  'projectorLeft',
  'projectorRight',
  'bodyMain'
] as const;

export type ThemeVisualBlockKey = typeof ThemeVisualBlockKeys[number];
export type ThemeVisualLayer = 'image' | 'svg';
export type ThemeVisualBackgroundKey = 'team1' | 'team2' | 'projector' | 'roundSummary' | 'endOfGameSummary';

export interface ThemeVisualBlockDraft {
  image: number | null;
  svg: number | null;
}

export interface ThemeVisualAssignmentsDraft {
  backgrounds: Record<ThemeVisualBackgroundKey, number | null>;
  blocks: Record<ThemeVisualBlockKey, ThemeVisualBlockDraft>;
}

export const ThemeVisualSlots: Array<{
  key: ThemeVisualBlockKey;
  label: string;
  size: 'header-team' | 'header-projector' | 'body';
}> = [
  { key: 'team1Left', label: 'Team 1 Left', size: 'header-team' },
  { key: 'team1Right', label: 'Team 1 Right', size: 'header-team' },
  { key: 'team2Left', label: 'Team 2 Left', size: 'header-team' },
  { key: 'team2Right', label: 'Team 2 Right', size: 'header-team' },
  { key: 'projectorLeft', label: 'Projector Left', size: 'header-projector' },
  { key: 'projectorRight', label: 'Projector Right', size: 'header-projector' },
  { key: 'bodyMain', label: 'Body Main', size: 'body' }
];

export const ThemeVisualBackgroundSlots = [
  { key: 'team1', label: 'Team 1 Background' },
  { key: 'team2', label: 'Team 2 Background' },
  { key: 'projector', label: 'Projector Background' },
  { key: 'roundSummary', label: 'Round Summary Background' },
  { key: 'endOfGameSummary', label: 'EOG Summary Background' }
] as const;

export const ThemeVisualMaxFileSizeBytes = 100 * 1024 * 1024;

export function createEmptyThemeVisualAssignments(): ThemeVisualAssignmentsDraft {
  return {
    backgrounds: { team1: null, team2: null, projector: null, roundSummary: null, endOfGameSummary: null },
    blocks: Object.fromEntries(
      ThemeVisualBlockKeys.map(key => [key, { image: null, svg: null }])
    ) as Record<ThemeVisualBlockKey, ThemeVisualBlockDraft>
  };
}

export function isAllowedThemeVisualArtifact(
  artifact: Pick<TeamMemberArtifactDto, 'contentType' | 'fileName'>,
  layer: ThemeVisualLayer
): boolean {
  const contentType = (artifact.contentType ?? '').toLowerCase().trim();
  const fileName = (artifact.fileName ?? '').toLowerCase().trim();

  if (layer === 'svg') {
    return contentType === 'image/svg+xml' || fileName.endsWith('.svg');
  }

  return (
    contentType === 'image/png'
    || contentType === 'image/jpeg'
    || contentType === 'image/jpg'
    || fileName.endsWith('.png')
    || fileName.endsWith('.jpg')
    || fileName.endsWith('.jpeg')
  );
}

export function isSafeSvgContent(content: string): boolean {
  const allowedTags = /^(?:svg|g|path|rect|circle|ellipse|line|polyline|polygon|defs|linearGradient|radialGradient|stop|clipPath|mask|use)$/i;
  const allowedAttributes = /^(?:id|viewBox|x|y|x1|x2|y1|y2|cx|cy|r|rx|ry|width|height|d|points|fill|fill-rule|fill-opacity|stroke|stroke-width|stroke-linecap|stroke-linejoin|stroke-opacity|opacity|offset|stop-color|stop-opacity|transform|preserveAspectRatio|clip-path|mask|href|xlink:href)$/i;

  if (!content.trim() || !/^\s*<svg\b/i.test(content) || !/<\/svg>\s*$/i.test(content)) {
    return false;
  }

  for (const match of content.matchAll(/<\/?([\w:-]+)(?:\s[^<>]*?)?\s*\/?>/g)) {
    if (!allowedTags.test(match[1])) {
      return false;
    }
  }

  if (/<\/svg>[^\s]/i.test(content) || /<[^>]*\s(?:style|on[a-z]+)\s*=/i.test(content)) {
    return false;
  }

  for (const match of content.matchAll(/\s([\w:-]+)\s*=\s*(["'])((?:(?!\2).)*)\2/gs)) {
    const attribute = match[1];
    const value = match[3];
    if (!allowedAttributes.test(attribute)
      || /(?:javascript:|data:|https?:|file:|\\\\|\/\/)/i.test(value)
      || ((attribute === 'href' || attribute === 'xlink:href') && !value.startsWith('#'))) {
      return false;
    }
  }

  return !/<(?:script|foreignObject|iframe|object|embed|animate|set|audio|video)\b/i.test(content);
}

export function validateThemeVisualFile(file: Pick<File, 'name' | 'type' | 'size'>): string | null {
  const isSvg = file.type.toLowerCase() === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg');
  const isRaster = ['image/png', 'image/jpeg', 'image/jpg'].includes(file.type.toLowerCase())
    || /\.(?:png|jpe?g)$/i.test(file.name);

  if (!isSvg && !isRaster) {
    return 'Theme visuals must be PNG, JPG/JPEG, or SVG files.';
  }

  if (file.size > ThemeVisualMaxFileSizeBytes) {
    return 'Theme visual files must be 100 MB or smaller.';
  }

  return null;
}
